import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Shield, Download, FileText, Lock, ChevronDown, AlertTriangle, Mail } from "lucide-react";

export default function SecurityPage() {
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [dsarType, setDsarType] = useState("");
  const [dsarDesc, setDsarDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/security/policies");
        setPolicies(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const submitDsar = async () => {
    if (!dsarType) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/security/dsar", { type: dsarType, description: dsarDesc });
      alert(data.message);
      setDsarType("");
      setDsarDesc("");
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setSubmitting(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/security/my-data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "stratum_data_export.json";
      a.click();
    } catch (e) { alert("Error exporting data"); }
  };

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="security-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Shield size={24} className="inline mr-2 text-[#C8A961]" />
            Security & POPIA Compliance
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">All security policies, data protection rights, and compliance documentation</p>
        </div>
        <button onClick={exportData} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] flex items-center gap-2" data-testid="export-my-data-btn">
          <Download size={14} /> Export My Data
        </button>
      </div>

      {/* Security Status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Lock, label: "Encryption", value: "AES-256 + TLS 1.3" },
          { icon: Shield, label: "Auth", value: "JWT + bcrypt + brute-force protection" },
          { icon: FileText, label: "Audit Trail", value: "Immutable, append-only" },
          { icon: AlertTriangle, label: "Data Residency", value: "South Africa (Cape Town)" },
        ].map(s => (
          <div key={s.label} className="stratum-card p-4">
            <s.icon size={16} className="text-[#C8A961] mb-2" />
            <div className="text-xs text-[#6B7280]">{s.label}</div>
            <div className="text-sm font-medium text-[#0B1020] mt-0.5">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Policy Documents */}
      <div className="stratum-card overflow-hidden">
        <div className="p-4 border-b border-[#E8E2D5] bg-[#FAF8F4]">
          <h3 className="font-display text-base font-semibold text-[#0B1020]">Policy Documents</h3>
          <p className="text-xs text-[#6B7280] mt-1">Click to view full policy. All documents available for download.</p>
        </div>
        <div className="divide-y divide-[#E8E2D5]">
          {policies?.policies?.map(p => (
            <div key={p.id}>
              <button onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-[#FAF8F4] transition-colors" data-testid={`policy-${p.id}`}>
                <div>
                  <h4 className="text-sm font-medium text-[#0B1020]">{p.title}</h4>
                  <p className="text-xs text-[#6B7280] mt-0.5">{p.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-[#6B7280]">Updated {p.last_updated}</span>
                  <ChevronDown size={14} className={`text-[#6B7280] transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {expanded === p.id && (
                <div className="px-5 pb-5 bg-[#FAF8F4]/50 border-t border-[#E8E2D5]">
                  <div className="space-y-2 mt-3">
                    {p.sections.map((s, i) => (
                      <p key={i} className="text-sm text-[#1A1A1A] leading-relaxed">{s}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* DSAR Form */}
      <div className="stratum-card p-6">
        <h3 className="font-display text-base font-semibold text-[#0B1020] mb-2">Data Subject Access Request (POPIA Section 23)</h3>
        <p className="text-xs text-[#6B7280] mb-4">Exercise your rights under POPIA. We respond within 30 days.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Request Type</label>
            <select className="stratum-input w-full" value={dsarType} onChange={e => setDsarType(e.target.value)} data-testid="dsar-type-select">
              <option value="">Select type...</option>
              <option value="ACCESS">Access my data (Section 23)</option>
              <option value="RECTIFICATION">Correct my data (Section 24)</option>
              <option value="ERASURE">Delete my data (Section 24)</option>
              <option value="PORTABILITY">Export my data (Section 24)</option>
              <option value="RESTRICTION">Restrict processing</option>
              <option value="OBJECTION">Object to processing (Section 11)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Description (optional)</label>
            <input className="stratum-input w-full" value={dsarDesc} onChange={e => setDsarDesc(e.target.value)} placeholder="Any specific details..." />
          </div>
        </div>
        <button onClick={submitDsar} disabled={submitting || !dsarType} className="stratum-btn-primary mt-4 disabled:opacity-50" data-testid="submit-dsar-btn">
          {submitting ? "Submitting..." : "Submit DSAR"}
        </button>
      </div>

      {/* Contact */}
      <div className="stratum-card p-5 bg-[#0B1020] text-white rounded-lg">
        <div className="flex items-center gap-3">
          <Mail size={20} className="text-[#C8A961]" />
          <div>
            <p className="text-sm font-medium">Information Officer: {policies?.information_officer}</p>
            <p className="text-xs text-white/50">Contact: {policies?.contact} | Regulator: {policies?.regulator}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
